// Read-only browser checks the model grounds its guidance in. None of them change anything.

import { MediaPermissionState } from '../../../../../../pure-js/types/AppAssistant'
import { type ClientPerformanceReport, measureClientPerformance } from '../../../libs/clientPerformance'

export type ConnectionDiagnostic = ClientPerformanceReport

export type MediaDiagnostic = {
  permission: MediaPermissionState
  deviceCount: number
}

const PERMISSION_STATES: MediaPermissionState[] = Object.values(MediaPermissionState)

const toPermissionState = (state: string): MediaPermissionState =>
  PERMISSION_STATES.find((known) => known === state) ?? MediaPermissionState.UNKNOWN

async function queryPermission(name: string): Promise<MediaPermissionState> {
  try {
    const permissions = navigator.permissions as
      | { query?: (opts: { name: PermissionName }) => Promise<{ state: string }> }
      | undefined
    if (!permissions?.query) return MediaPermissionState.UNKNOWN
    const status = await permissions.query({ name: name as PermissionName })
    return toPermissionState(status.state)
  } catch {
    return MediaPermissionState.UNKNOWN
  }
}

async function countInputDevices(kind: MediaDeviceKind): Promise<number> {
  try {
    if (!navigator.mediaDevices?.enumerateDevices) return 0
    const devices = await navigator.mediaDevices.enumerateDevices()
    return devices.filter((device) => device.kind === kind).length
  } catch {
    return 0
  }
}

/** Both halves of "it's laggy": the link, and how busy this machine is. Advice needs to tell them apart. */
export function checkConnection(): Promise<ConnectionDiagnostic> {
  return measureClientPerformance()
}

/** Permission alone, without the device list: `enumerateDevices()` takes a noticeable moment, and a
 * caller that only needs to know whether it may start must not wait on a count it never reads. */
export function checkMicrophonePermission(): Promise<MediaPermissionState> {
  return queryPermission('microphone')
}

export async function checkMicrophone(): Promise<MediaDiagnostic> {
  const [permission, deviceCount] = await Promise.all([queryPermission('microphone'), countInputDevices('audioinput')])
  return { permission, deviceCount }
}

export async function checkCamera(): Promise<MediaDiagnostic> {
  const [permission, deviceCount] = await Promise.all([queryPermission('camera'), countInputDevices('videoinput')])
  return { permission, deviceCount }
}
